import { Wallet as WalletIcon, Sparkles, Package } from 'lucide-react';
import { memberPortal, useAsync } from '@fitboxx/shared';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface WalletState {
  balance?: { credits?: number };
  active_packs?: Array<{ id: string; package_name?: string; credits_remaining?: number; expires_at?: string }>;
}

export default function Wallet() {
  const { data, loading } = useAsync(() => memberPortal.wallet() as Promise<WalletState>);
  const credits = data?.balance?.credits ?? 0;
  const packs = data?.active_packs ?? [];

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold tracking-tight">Wallet</h1>

      {loading ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <Card className="p-6 bg-gradient-to-br from-primary/15 to-primary/5">
          <div className="flex items-center gap-3 text-muted-foreground text-xs uppercase tracking-wider font-medium">
            <WalletIcon className="h-4 w-4" />Credits balance
          </div>
          <div className="text-5xl font-semibold tabular-nums mt-2">{credits}</div>
          <div className="text-xs text-muted-foreground mt-1">
            {packs.length === 0 ? 'No active packs' : `${packs.length} active pack${packs.length === 1 ? '' : 's'}`}
          </div>
        </Card>
      )}

      <div className="space-y-2">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Active packs</h2>
        {loading ? <Skeleton className="h-20 w-full" /> :
          packs.length === 0 ? (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              <Sparkles className="h-6 w-6 mx-auto mb-2 text-muted-foreground/60" />
              You don't have any class packs yet.
              <div className="mt-3">
                <a href="/shop" className="text-primary font-medium hover:underline">Browse packs in Shop →</a>
              </div>
            </Card>
          ) : packs.map((p) => (
            <Card key={p.id} className="p-3 flex items-center gap-3">
              <Package className="h-5 w-5 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{p.package_name || 'Pack'}</div>
                {p.expires_at && (
                  <div className="text-xs text-muted-foreground">
                    Expires {new Date(p.expires_at).toLocaleDateString()}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-2xl font-semibold tabular-nums">{p.credits_remaining ?? 0}</div>
                <div className="text-[10px] uppercase text-muted-foreground tracking-wider">credits</div>
              </div>
            </Card>
          ))}
      </div>
    </div>
  );
}
